<template>
  <v-combobox
    outlined
    class="zoox-component zoox-combobox"
    :style="inputRadius"
    :clearable="clearable"
    :class="{small, chips}"
    :color="color"
    :deletable-chips="deletableChips"
    :chips="chips"
    :dense="isDense"
    :small-chips="chips"
    :multiple="multiple"
    :items="items"
    :value="value"
    :append-icon="appendIcon"
    :hint="hint"
    :rules="rules"
    :label="label"
    :search-input="searchInput"
    :hide-details="hideDetails"
    :filter="filterTextItems"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-if="$scopedSlots.item" #item="{item, ...props}">
      <!--
         @slot Define uma aparencia customizada para o item
            @binding {object} item Objeto referente ao item passado
      -->
      <slot name="item" :item="item" v-bind="props"></slot>
    </template>

    <template
      v-if="$scopedSlots.selection || chips"
      #selection="{item, ...props}"
    >
      <!-- @slot Define uma aparencia customizada para o selection 
            @binding {object} item Objeto referente ao item passado
      -->
      <slot name="selection" :item="item" v-bind="props">
        <z-chip
          :x-small="!large"
          :color="color || 'primary'"
          selected
          :text="item.text"
        />
      </slot>
    </template>

    <template v-if="$scopedSlots['no-data']" #no-data>
      <!-- @slot Define uma aparencia customizada para o texto de   -->
      <slot name="no-data"></slot>
    </template>
  </v-combobox>
</template>

<script>
/**
 * Componente que extende as funcionalidades do componente **z-auto-complete**, com a possibilidade de adicionar novos itens.
 * @displayName Combobox
 */
export default {
  name: 'ZCombobox',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /**
     * Especifica quais itens estarão no combobox
     *
     * @typedef Object {text:string,value:any}
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * Aplica o estilo de chip no item selecionado
     */
    chips: {
      type: Boolean,
      default: false,
    },
    /**
     *  Aplica um icone para remover o chip quando a prop `chips` é usada
     */
    deletableChips: {
      type: Boolean,
      default: false,
    },
    /**
     * Quando true, multiplos itens podem ser selecionados
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar se o combobox é clearable ou não
     */
    clearable: {
      type: Boolean,
      default: true,
    },
    /**
     * Determina um valor para o input
     *
     * @modelName value
     */
    value: {
      type: [Number, String, Object, Array],
      required: false,
    },
    /**
     * Define o ícone em frente ao input
     */
    appendIcon: {
      type: String,
      required: false,
    },
    /**
     * Hint para o input
     */
    hint: {
      type: String,
      required: false,
    },
    /**
     * Label do auto complete
     */
    label: {
      type: String,
      required: false,
    },
    /**
     * Regras associadas com o input
     */
    rules: {
      type: Array,
      required: false,
    },
    /**
     * Flag para esconder div de mensagens e hint
     */
    hideDetails: {
      type: [Boolean, String],
      required: false,
    },
    /**
     * Valor do input de busca
     */
    searchInput: {
      type: String,
      default: '',
    },
    /**
     * Define o texto quando não existe dados disponiveis
     */
    noDataText: {
      type: String,
    },
    /**
     * Flag para indicar que o input deve ser pequeno (defaults: {mobile: false, desktop: false})
     */
    small: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser medio (defaults: {mobile: false, desktop: true})
     */
    medium: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser grande (defaults: {mobile: true, desktop: false})
     */
    large: {
      type: Boolean,
      required: false,
    },
    /**
     * Cor do input e dos chips se for usado no modo chips
     */
    color: {
      type: String,
      default: 'primary',
    },
    /**
     * Define o border-radius do input
     */
    radius: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    isDense() {
      const isMobile = this.$vuetify.breakpoint.xs

      if (!this.small && !this.medium && !this.large) {
        return isMobile ? false : true
      }

      if (this.large) {
        return false
      }

      return this.small || this.medium
    },

    inputRadius() {
      if (this.radius) {
        return `border-radius: ${this.radius}px !important`
      }

      if (this.small) {
        return 'border-radius: var(--component-input-sm-radius) !important'
      }

      return 'border-radius: var(--component-input-md-radius) !important'
    },
  },
  methods: {
    onSearchInput(value) {
      /**
       * Evento disparado ao digitar no input de busca
       *
       * @property {string} value - Valor digitado no input
       */
      this.$emit('update:search-input', value)
    },
    onChange(value) {
      /**
       * Evento disparado ao selecionar um item
       *
       * @property {any} value - Valor selecionado no input
       */
      this.$emit('change', value)
    },

    onBlur() {
      /**
       * Evento disparado ao sair do input
       */
      this.$emit('blur')
    },

    // normaliza os itens buscados
    filterTextItems(_, queryText, itemText) {
      return itemText
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .includes(
          queryText
            .toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        )
    },
  },
}
</script>
<style lang="scss" scoped>
.v-list {
  padding: 0 !important;
}

.zoox-combobox {
  border-radius: var(--component-input-md-radius) !important;
}
</style>

<style lang="scss">
@use '@scss/global' as g;

.zoox-combobox {
  &.small {
    @include g.zoox-small-form-input;

    .v-select__selections {
      font-size: var(--zoox-font-size-body3);
    }

    &.chips {
      .v-select__selections {
        padding: 6px 0 0 2px !important;
      }
    }
  }
}
</style>
